import React from "react";
import { Link, useParams } from "react-router-dom";
import { Failure, Loading, useData, useTitle, formatDate } from "../editorial/hooks";
import { Page, Story } from "../editorial/types";
import MarkdownBody from "../editorial/MarkdownBody";
import ArticleCard from "./ArticleCard";
import Byline, { Avatar } from "./Byline";
import { Article, ArticleSummary, BlogHomeData, CATEGORIES, categoryLabel } from "./types";

function CategoryStrip({ current }: { current?: string }) {
  return (
    <nav className="bl-categories" aria-label="Blog categories">
      <Link to="/blog" aria-current={!current ? "page" : undefined}>
        All articles
      </Link>
      {CATEGORIES.map((c) => (
        <Link key={c.key} to={"/blog/category/" + c.key} aria-current={current === c.key ? "page" : undefined}>
          {c.label}
        </Link>
      ))}
    </nav>
  );
}

// Three highlighted articles: the lead on the left, up to two supporting
// articles on the right. Used by the Blog front page and by "Commentary and Stories" on the
// Research home page. With nothing to support the lead, the right column
// introduces the four categories instead of standing empty.
export function LeadGroup({ lead, supporting }: { lead: ArticleSummary | null; supporting: ArticleSummary[] }) {
  if (!lead) return null;
  return (
    <div className="bl-lead-grid">
      <ArticleCard article={lead} lead />
      <div className="bl-side">
        {supporting.map((a) => (
          <ArticleCard key={a.id} article={a} compact />
        ))}
        {supporting.length === 0 && (
          <div className="bl-about">
            <div className="er-kicker">In the Blog</div>
            {CATEGORIES.map((c) => (
              <p key={c.key}>
                <Link to={"/blog/category/" + c.key}>{c.label}</Link> · {c.blurb}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogHome() {
  const { value, error } = useData<BlogHomeData>("/blog/home");
  useTitle("Beyond the project | idecRxiv");
  return (
    <>
      <div className="er-breadcrumb">
        <Link to="/">Home</Link> / Blog
      </div>
      <div className="er-intro">
        <div className="er-kicker">Blog · opinion and stories from the iDEC community</div>
        <h1>Beyond the project</h1>
        <p>
          Scientific Perspectives · iDEC Voices · Judges’ Perspectives · Industry Insights
        </p>
      </div>
      <CategoryStrip />
      <Failure text={error} />
      {!value && !error && <Loading />}
      {value && !value.lead && <p className="er-empty">No articles have been published yet.</p>}
      {value && value.lead && (
        <section className="bl-featured">
          <div className="er-section-head">
            <div>
              <h2>{value.lead.category === "perspectives" ? "Scientific Perspectives" : "Featured"}</h2>
              <p>
                {value.lead.category === "perspectives"
                  ? "The latest scientific questions, viewpoints and discussion"
                  : "The newest article in the Blog"}
              </p>
            </div>
            {value.lead.category === "perspectives" && <Link to="/blog/category/perspectives">All Scientific Perspectives →</Link>}
          </div>
          <LeadGroup lead={value.lead} supporting={value.supporting} />
        </section>
      )}
      {value && value.latest.some((l) => l.item) && (
        <section className="bl-section bl-latest">
          <div className="er-section-head">
            <div>
              <h2>From the iDEC community</h2>
              <p>The newest article in each of the other categories</p>
            </div>
          </div>
          <div className="bl-grid">
            {value.latest.map((l) =>
              l.item ? (
                <ArticleCard key={l.key} article={l.item} />
              ) : (
                <div key={l.key} className="bl-card bl-empty">
                  <div className="er-kicker">
                    <Link to={l.url}>{l.label}</Link>
                  </div>
                  <p>{CATEGORIES.find((c) => c.key === l.key)?.blurb} Nothing published here yet.</p>
                </div>
              )
            )}
          </div>
        </section>
      )}
      {value && value.more.length > 0 && (
        <section className="bl-section">
          <div className="er-section-head">
            <div>
              <h2>More articles</h2>
              <p>{value.more.length} more article{value.more.length === 1 ? "" : "s"} · newest first</p>
            </div>
          </div>
          <div className="bl-grid">
            {value.more.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

export function CategoryPage() {
  const { key } = useParams<{ key: string }>(),
    label = categoryLabel(key),
    { value, error } = useData<Page<ArticleSummary>>(
      "/blog/articles?category=" + encodeURIComponent(key) + "&limit=100",
    );
  useTitle((label || "Blog") + " | idecRxiv");
  if (!label)
    return (
      <>
        <h1>Category not found</h1>
        <Link to="/blog">Back to the Blog</Link>
      </>
    );
  return (
    <>
      <div className="er-breadcrumb">
        <Link to="/">Home</Link> / <Link to="/blog">Blog</Link> / {label}
      </div>
      <div className="er-intro">
        <div className="er-kicker">Blog</div>
        <h1>{label}</h1>
        <p>{CATEGORIES.find((c) => c.key === key)?.blurb}</p>
      </div>
      <CategoryStrip current={key} />
      <Failure text={error} />
      {!value && !error && <Loading />}
      {value && value.items.length === 0 && <p className="er-empty">No articles in this category yet.</p>}
      {value && (
        <div className="bl-grid">
          {value.items.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      )}
    </>
  );
}

function ProjectBox({ project, count }: { project: Story; count?: number }) {
  return (
    <aside className="bl-project">
      <div className="er-kicker">Related project · iDEC {project.year} · {project.team}</div>
      <h3>{project.headline}</h3>
      <div className="er-links">
        <a href={project.wikiUrl} target="_blank" rel="noopener noreferrer">
          Explore Wiki ↗
        </a>
        {project.articles.map((a, i) => (
          <a key={a.articleId} href={a.url}>
            Read preprint{project.articles.length > 1 ? " " + (i + 1) : ""} →
          </a>
        ))}
        {count !== undefined && count > 1 && (
          <Link to={"/blog/project/" + project.id}>All articles about this project ({count}) →</Link>
        )}
        <Link to="/inside-the-research">Inside the Research →</Link>
      </div>
    </aside>
  );
}

export function ArticleBody({ value, preview = false }: { value: Article; preview?: boolean }) {
  return (
    <article className="er-blog bl-article">
      <div className="er-breadcrumb">
        <Link to="/">Home</Link> / <Link to="/blog">Blog</Link> /{" "}
        <Link to={value.categoryUrl}>{value.categoryLabel}</Link>
      </div>
      <div className="er-kicker">{value.categoryLabel}</div>
      <h1>{value.title || (preview ? "Untitled article" : "")}</h1>
      {value.standfirst && <p className="er-standfirst">{value.standfirst}</p>}
      <Byline author={value.author} date={value.publishedAt} size={44} />
      {value.cover && (
        <figure className="bl-cover">
          <img src={value.cover.url} alt={value.cover.alt} />
          {value.cover.caption && <figcaption>{value.cover.caption}</figcaption>}
        </figure>
      )}
      <MarkdownBody text={value.bodyMarkdown} />
      {(value.author.bio || value.author.role) && (
        <aside className="bl-author-box">
          <Avatar author={value.author} size={56} />
          <div>
            <div className="er-kicker">About the author</div>
            <p className="bl-author">{value.author.name}</p>
            {value.author.role && <p className="bl-role">{value.author.role}</p>}
            {value.author.bio && <p>{value.author.bio}</p>}
          </div>
        </aside>
      )}
      {value.project && <ProjectBox project={value.project} count={value.project.relatedArticles?.count} />}
      <p className="bl-back">
        <Link to={value.categoryUrl}>← More {value.categoryLabel}</Link>
      </p>
    </article>
  );
}

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>(),
    { value, error } = useData<Article>("/blog/articles/" + encodeURIComponent(slug));
  useTitle(value ? value.title + " | idecRxiv" : "Blog | idecRxiv");
  return error ? (
    <>
      <Failure text={error} />
      <p>
        <Link to="/blog">Back to the Blog</Link>
      </p>
    </>
  ) : !value ? (
    <Loading />
  ) : (
    <ArticleBody value={value} />
  );
}

export function ProjectArticlesPage() {
  const { projectId } = useParams<{ projectId: string }>(),
    { value, error } = useData<{ project: Story; items: ArticleSummary[] }>(
      "/blog/project/" + encodeURIComponent(projectId),
    );
  useTitle(value ? "Articles about " + value.project.team + " | idecRxiv" : "Blog | idecRxiv");
  if (error)
    return (
      <>
        <Failure text={error} />
        <p>
          <Link to="/blog">Back to the Blog</Link>
        </p>
      </>
    );
  if (!value) return <Loading />;
  const p = value.project;
  return (
    <>
      <div className="er-breadcrumb">
        <Link to="/">Home</Link> / <Link to="/inside-the-research">Inside the Research</Link> / Related articles
      </div>
      <div className="er-intro bl-project-head">
        <div className="er-kicker">Related articles · iDEC {p.year} · {p.team}</div>
        <h1>{p.headline}</h1>
        <div className="er-links">
          <a href={p.wikiUrl} target="_blank" rel="noopener noreferrer">
            Explore Wiki ↗
          </a>
          {p.articles.map((a, i) => (
            <a key={a.articleId} href={a.url}>
              Read preprint{p.articles.length > 1 ? " " + (i + 1) : ""} →
            </a>
          ))}
          <Link to="/inside-the-research">← Inside the Research</Link>
        </div>
      </div>
      <p className="er-team" aria-live="polite">
        {value.items.length} published article{value.items.length === 1 ? "" : "s"} across all categories
      </p>
      <div className="bl-grid">
        {value.items.map((a) => (
          <ArticleCard key={a.id} article={a} showProject={false} />
        ))}
      </div>
    </>
  );
}

export function ArticlePreviewPage() {
  const { id } = useParams<{ id: string }>(),
    { value, error } = useData<Article>("/admin/articles/" + encodeURIComponent(id) + "/preview");
  useTitle("Draft preview | idecRxiv");
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  if (error)
    return (
      <>
        <Failure text={error} />
        <p>
          <Link to="/editor">Go to the editorial desk</Link>
        </p>
      </>
    );
  if (!value) return <Loading />;
  return (
    <>
      <div className="er-preview-banner" role="status">
        <strong>Draft preview · editors only.</strong> This is the saved draft, not the public site.{" "}
        {value.publishedAt ? "Published " + formatDate(value.publishedAt) + "." : "Not yet published."}{" "}
        <Link to="/editor">Back to the desk</Link>
      </div>
      <ArticleBody value={value} preview />
    </>
  );
}
